import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Table } from 'antd';
import ContentHeader from '../../components/ContentHeader';
import { Page } from '../../components/layout';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { ZorbaSelect, ZorbaButton } from '../../components/forms';
import { BUTTON_VARIANT_DARK } from '../../components/forms/ZorbaButton/constants';
import { showErrorToast } from '../../services/notificationsService';
import { getRequest, postRequest } from '../../services/requestService';
import { daysOptions, getTableColumns } from './constants';
import { updateUserDetails } from '../../reducers/Auth/actions';
import { WhitelabelAcceptTermsAndConditionsModal } from '../../containers/layout/Components/WhitelabelAcceptAdminTermsAndConditionsModal';

import './index.scss';

const MarketplaceReportPage = () => {
  useDocumentTitle('Admin Overview');

  const history = useHistory();
  const dispatch = useDispatch();
  const { userDetails } = useSelector(({ AuthReducer }) => AuthReducer);
  const [selectedDays, setSelectedDays] = useState(30);
  const managedMarketplace =
    userDetails.defaultOrganization?.managedMarketplace;
  const areTermsSigned =
    userDetails?.defaultOrganization?.whiteLabelAdminTermsOfUse?.signedAt;

  const [selectedMarketplace, setSelectedMarketplace] = useState(
    managedMarketplace,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isMarketplacesLoading, setIsMarketplacesLoading] = useState(false);
  const [data, setData] = useState([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState([]);

  const isZorbaUser = userDetails.email.includes('getzorba.com');

  useEffect(() => {
    if (!managedMarketplace) {
      history.push('/');
    }
  }, [managedMarketplace]);

  const fetchMarketplaces = useCallback(async () => {
    setIsMarketplacesLoading(true);
    try {
      const response = await getRequest('/backoffice/whitelabel/list');
      const partners = response?.data?.partners || [];
      const options = partners
        .filter(
          ({ partnerKey, partnerName, isActive }) =>
            Boolean(partnerKey) && Boolean(partnerName) && isActive !== false,
        )
        .map(({ partnerKey, partnerName, name }) => ({
          label: partnerName || name || partnerKey,
          value: partnerKey,
        }));
      setMarketplaceOptions(options);
    } catch (error) {
      showErrorToast('Failed to load marketplaces');
      setMarketplaceOptions([]);
    }
    setIsMarketplacesLoading(false);
  }, []);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await postRequest('/offer/get-marketplace-report', {
        selectedDays,
      });
      setData(response?.data?.data || []);
    } catch (error) {
      showErrorToast('Fetching report failed');
    }
    setIsLoading(false);
  };

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) {
      showErrorToast('No data to export');
      return;
    }

    try {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      const todayFormatted = `${month}-${day}-${year}`;

      const csvData = data.map((item) => ({
        Buyer: item.buyer || '',
        Email: item.email || '',
        'Phone Number': item.phoneNumber || '',
        'Did Activate Buy Box': item.didActivateBuyBox ? 'Yes' : 'No',
        'Last Login': item.lastLogin || '',
        'Deals Received': item.requestedOffers || 0,
        Offers: item.offers || 0,
        'Accepted by Seller': item.acceptedOffers || 0,
        ...(isZorbaUser && {
          'Did Not Respond': item.didNotRespond || 0,
          'Rejected By Buyer': item.rejectedByBuyer || 0,
        }),
      }));

      const headers = Object.keys(csvData[0]);
      const csvRows = [headers];

      csvData.forEach((row) => {
        const values = headers.map((header) => {
          const value = row[header];
          if (value === null || value === undefined) {
            return '';
          }
          const stringValue = String(value);
          if (
            stringValue.includes(',') ||
            stringValue.includes('"') ||
            stringValue.includes('\n')
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values);
      });

      const csvContent = csvRows.map((row) => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `marketplace-report-${todayFormatted}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showErrorToast('Export failed');
    }
  };

  useEffect(() => {
    if (areTermsSigned) {
      fetchReport();
    }
  }, [selectedDays, areTermsSigned]);

  useEffect(() => {
    if (isZorbaUser) {
      fetchMarketplaces();
    }
  }, [fetchMarketplaces, isZorbaUser]);

  const pagination = {
    position: ['bottomRight'],
    pageSize: 50,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  };

  const handleChangeCompanyType = (value) => {
    setSelectedMarketplace(value);
    setIsLoading(true);
    dispatch(
      updateUserDetails(
        {
          managedMarketplace: value,
        },
        () => fetchReport(),
      ),
    );
  };

  return (
    <Page className="marketplace-report">
      <ContentHeader title="Admin Overview" />
      <div className="marketplace-report-filter">
        <ZorbaSelect
          options={daysOptions}
          value={selectedDays}
          onChange={(e) => setSelectedDays(e)}
          className="marketplace-report-select"
          disabled={isLoading}
        />
        {isZorbaUser && (
          <ZorbaSelect
            options={marketplaceOptions}
            value={selectedMarketplace}
            onChange={(value) => handleChangeCompanyType(value)}
            className="marketplace-report-select"
            disabled={isLoading || isMarketplacesLoading}
          />
        )}
        <ZorbaButton
          variant={BUTTON_VARIANT_DARK}
          onClick={handleDownloadCSV}
          disabled={isLoading || !areTermsSigned || data.length === 0}
          fullWidth={false}
        >
          Download CSV
        </ZorbaButton>
      </div>
      <Table
        rowKey="_id"
        className="marketplace-report-table"
        dataSource={data}
        columns={getTableColumns({ isZorbaUser })}
        loading={isLoading}
        pagination={pagination}
        scroll={{
          y: 720,
        }}
      />
      <WhitelabelAcceptTermsAndConditionsModal fetchReport={fetchReport} />
    </Page>
  );
};

export default MarketplaceReportPage;
